import { type ReactNode, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

type RevealProps = {
  children: ReactNode;
  className?: string;
  direction?: "up" | "left" | "right";
  delay?: 0 | 1 | 2 | 3;
};

export const CaseStudyReveal = ({ children, className, direction = "up", delay = 0 }: RevealProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(element);
        }
      },
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={cn(
        "case-study-reveal",
        `case-study-reveal--${direction}`,
        `case-study-reveal--delay-${delay}`,
        isVisible && "is-visible",
        className,
      )}
    >
      {children}
    </div>
  );
};

type MediaProps = {
  children: ReactNode;
  caption?: string;
  className?: string;
  frameClassName?: string;
  captionClassName?: string;
};

export const CaseStudyMedia = ({ children, caption, className, frameClassName, captionClassName }: MediaProps) => (
  <figure className={cn("case-study-media group/media", className)}>
    <div className={cn("case-study-media__frame", frameClassName)}>{children}</div>
    {caption && (
      <figcaption className={cn("case-study-media__caption", captionClassName)}>{caption}</figcaption>
    )}
  </figure>
);